import { useState, useCallback, useMemo } from 'react';
import useRailFilters from './useRailFilters';

/**
 * useBaseballRailFilters - Baseball-specific rail filter configuration
 * Provides tabs, filters, and handlers for baseball dashboard
 * 
 * @param {Function} onFiltersChange - Callback when filters change
 * @param {Date} initialDate - Initial date value
 * @param {Object} initialLeague - Initial league value
 * @param {number} initialSeason - Initial season value
 */
const useBaseballRailFilters = ({ 
  onFiltersChange, 
  initialDate,
  initialLeague = { id: 1, name: 'MLB' },
  initialSeason = new Date().getFullYear()
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [showDateSheet, setShowDateSheet] = useState(false);
  const [showLeagueSheet, setShowLeagueSheet] = useState(false);
  const [showSeasonSheet, setShowSeasonSheet] = useState(false);
  
  // Default filter values - captured once on mount, never change
  const defaultFilters = useMemo(() => {
    // CRITICAL: Always ensure we have a valid date string
    let dateStr = initialDate;
    
    // If initialDate is already a string in YYYY-MM-DD format, use it
    if (typeof initialDate === 'string' && initialDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      dateStr = initialDate;
    } 
    // Otherwise create today's date string
    else {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }
    
    return {
      date: dateStr,
      league: initialLeague,
      season: initialSeason,
      division: 'all'
    };
  }, []); // Empty deps - capture initial values only once!
  
  // Use base rail filters hook
  const {
    filters,
    updateFilter,
    resetFilters,
    hasActiveFilters,
    buildFilterObjects
  } = useRailFilters({
    sport: 'baseball',
    defaultFilters,
    onFiltersChange
  });
  
  // Format date for display
  const formatDateLabel = useCallback((dateStr) => {
    if (!dateStr) return 'Select Date';
    
    // Handle if dateStr is actually a Date object
    if (dateStr instanceof Date) {
      const year = dateStr.getFullYear();
      const month = String(dateStr.getMonth() + 1).padStart(2, '0');
      const day = String(dateStr.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }
    
    // Validate the date string format
    if (typeof dateStr !== 'string' || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      console.warn('Invalid date format:', dateStr);
      return 'Select Date';
    }
    
    // Parse the date string correctly to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);
    
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);
    
    // Compare dates properly
    const dateCompare = new Date(date);
    dateCompare.setHours(12, 0, 0, 0);
    
    if (dateCompare.getTime() === today.getTime()) {
      return 'Today';
    } else if (dateCompare.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayName = days[date.getDay()];
      const monthNum = date.getMonth() + 1;
      const dayNum = date.getDate();
      return `${dayName} ${monthNum}/${dayNum}`;
    }
  }, []);
  
  // Format league for display
  const formatLeagueLabel = useCallback((league) => {
    if (!league || league === 'all') return 'All Leagues';
    if (typeof league === 'object') return league.name;
    return league;
  }, []);
  
  // Format season for display
  const formatSeasonLabel = useCallback((season) => {
    if (!season) return 'Select Season';
    return `${season} Season`;
  }, []);
  
  // Primary tabs configuration - Baseball divisions
  const primaryTabs = useMemo(() => {
    return [
      { id: 'all', label: 'All Games' },
      { id: 'al', label: 'American League' },
      { id: 'nl', label: 'National League' },
      { id: 'al-east', label: 'AL East' },
      { id: 'al-central', label: 'AL Central' },
      { id: 'al-west', label: 'AL West' },
      { id: 'nl-east', label: 'NL East' },
      { id: 'nl-central', label: 'NL Central' },
      { id: 'nl-west', label: 'NL West' }
    ];
  }, []);
  
  // Secondary filter configuration
  const filterConfig = useMemo(() => {
    const configs = [
      {
        id: 'date',
        type: 'date',
        getLabel: formatDateLabel,
        hasDropdown: true
      }
    ];
    
    // Only show league chip if "All Games" is selected
    if (activeTab === 'all') {
      configs.push({
        id: 'league',
        type: 'select',
        getLabel: formatLeagueLabel,
        hasDropdown: true
      });
    }
    
    // Add season chip
    configs.push({
      id: 'season',
      type: 'select',
      getLabel: formatSeasonLabel,
      hasDropdown: true
    });
    
    return configs;
  }, [activeTab, formatDateLabel, formatLeagueLabel, formatSeasonLabel]);
  
  // Build filter objects for SecondaryRail
  const filterObjects = buildFilterObjects(filterConfig);
  
  // Handle filter chip click
  const handleFilterClick = useCallback((filterId, filterData) => {
    switch (filterId) {
      case 'date':
        setShowDateSheet(true);
        break;
      case 'league':
        setShowLeagueSheet(true);
        break;
      case 'season':
        setShowSeasonSheet(true);
        break;
      default:
        break;
    }
  }, []);
  
  // Handle primary tab change
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    
    // Update division filter based on tab selection
    if (tabId !== 'all') {
      updateFilter('division', tabId);
    } else {
      updateFilter('division', 'all');
    }
  }, [updateFilter]);
  
  return {
    // State
    filters,
    activeTab,
    hasActiveFilters,
    
    // Tabs and filters
    primaryTabs,
    filterObjects,
    
    // Handlers
    handleTabChange,
    handleFilterClick,
    updateFilter,
    resetFilters,
    
    // Sheet visibility
    showDateSheet,
    setShowDateSheet,
    showLeagueSheet,
    setShowLeagueSheet,
    showSeasonSheet,
    setShowSeasonSheet
  };
};

export default useBaseballRailFilters;